import { z } from 'zod';
import { TERRA_ALWAYS_ALLOWED_DOMAINS } from './terra-always-allowed-domains';
import {
  extractHostname,
  extractRequestScopePath,
  isRegexPattern,
  isSubdomain,
  pathHasWildcardOnlyAtSegmentStartOrMiddle,
  patternMatchesHost,
  pathMatchesPattern,
} from './url-pattern-matching';

export type DomainScopeFlags = {
  isInFunctionalityScope: boolean;
  isInTestingScope: boolean;
  isThirdParty: boolean;
};

export type DomainScopeFlagsInput = {
  isInFunctionalityScope?: boolean | null;
  isInTestingScope?: boolean | null;
  isThirdParty?: boolean | null;
};

export const DOMAIN_SCOPE_MODES = [
  'attack',
  'interaction',
  'third_party',
  'none',
] as const;

export type DomainScopeMode = (typeof DOMAIN_SCOPE_MODES)[number];

export type DomainScopePathRuleKind = {
  scope?: 'ALLOWLIST' | 'BLOCKLIST';
  isThirdParty?: boolean;
};

export const isThirdPartyPathRule = (rule: DomainScopePathRuleKind): boolean =>
  rule.isThirdParty === true;

export const isHostRestrictingAllowlistRule = (
  rule: DomainScopePathRuleKind,
): boolean => rule.scope !== 'BLOCKLIST' && !isThirdPartyPathRule(rule);

export function scopeModeFromFlags({
  isInFunctionalityScope,
  isInTestingScope,
  isThirdParty,
}: DomainScopeFlagsInput): DomainScopeMode {
  if (isInTestingScope) {
    return 'attack';
  }

  if (isInFunctionalityScope) {
    return 'interaction';
  }

  if (isThirdParty) {
    return 'third_party';
  }

  return 'none';
}

export function scopeFlagsFromMode(mode: DomainScopeMode): DomainScopeFlags {
  switch (mode) {
    case 'attack':
      return {
        isInFunctionalityScope: true,
        isInTestingScope: true,
        isThirdParty: false,
      };
    case 'interaction':
      return {
        isInFunctionalityScope: true,
        isInTestingScope: false,
        isThirdParty: false,
      };
    case 'third_party':
      return {
        isInFunctionalityScope: false,
        isInTestingScope: false,
        isThirdParty: true,
      };
    case 'none':
      return {
        isInFunctionalityScope: false,
        isInTestingScope: false,
        isThirdParty: false,
      };
  }
}

export function resolveExclusiveDomainScopeFlags(
  flags: DomainScopeFlagsInput,
): DomainScopeFlags {
  return scopeFlagsFromMode(scopeModeFromFlags(flags));
}

export function resolveDomainScopeFlagsFromEntry({
  isInFunctionalityScope,
  isInTestingScope,
  isThirdParty,
  allowlistRules,
}: DomainScopeFlagsInput & {
  allowlistRules: Array<{
    isInFunctionalityScope: boolean;
    isInTestingScope: boolean;
    isThirdParty?: boolean;
    scope?: 'ALLOWLIST' | 'BLOCKLIST';
  }>;
}): DomainScopeFlags {
  const restrictingRules = allowlistRules.filter(
    isHostRestrictingAllowlistRule,
  );

  if (restrictingRules.length > 0) {
    return resolveExclusiveDomainScopeFlags({
      isInTestingScope: restrictingRules.some((rule) => rule.isInTestingScope),
      isInFunctionalityScope:
        (isInFunctionalityScope ?? false) ||
        restrictingRules.some((rule) => rule.isInFunctionalityScope),
      isThirdParty,
    });
  }

  return resolveExclusiveDomainScopeFlags({
    isInFunctionalityScope,
    isInTestingScope,
    isThirdParty,
  });
}

export const SCOPE_DIMENSIONS = [
  'functionality',
  'testing',
  'thirdParty',
] as const;

export type ScopeDimension = (typeof SCOPE_DIMENSIONS)[number];

const booleanDefaultFalse = z
  .boolean()
  .nullable()
  .optional()
  .transform((value) => value ?? false);

export const DomainPathRuleEntrySchema = z.object({
  path: z.string(),
  scope: z.enum(['ALLOWLIST', 'BLOCKLIST']),
  isInFunctionalityScope: z.boolean(),
  isInTestingScope: z.boolean(),
  isThirdParty: booleanDefaultFalse,
});

export const DomainScopeEntrySchema = z.object({
  domain: z.string(),
  isInFunctionalityScope: z.boolean().nullable().optional(),
  isInTestingScope: z.boolean().nullable().optional(),
  isThirdParty: z.boolean().nullable().optional(),
  pathRules: z.array(DomainPathRuleEntrySchema).optional(),
});

export type DomainPathRuleEntry = {
  path: string;
  scope: 'ALLOWLIST' | 'BLOCKLIST';
  isInFunctionalityScope: boolean;
  isInTestingScope: boolean;
  isThirdParty?: boolean;
};

export type DomainScopeEntry = {
  domain: string;
  isInFunctionalityScope?: boolean | null;
  isInTestingScope?: boolean | null;
  isThirdParty?: boolean | null;
  pathRules?: DomainPathRuleEntry[];
};

export type ParsedDomainScopeEntry = z.infer<typeof DomainScopeEntrySchema>;

const DomainPathRuleParseSchema = z.object({
  path: z.string(),
  scope: z.enum(['ALLOWLIST', 'BLOCKLIST']),
  isInFunctionalityScope: booleanDefaultFalse,
  isInTestingScope: booleanDefaultFalse,
  isThirdParty: booleanDefaultFalse,
});

const DomainScopeEntryParseSchema = DomainScopeEntrySchema.extend({
  pathRules: z.array(DomainPathRuleParseSchema).optional(),
});

export type DomainScopeParseInput = z.input<typeof DomainScopeEntryParseSchema>;

export const parseDomainScopeEntries = (
  entries?: DomainScopeParseInput[] | null,
): ParsedDomainScopeEntry[] => {
  const parsed = DomainScopeEntryParseSchema.array().safeParse(entries ?? []);

  return parsed.success ? parsed.data : [];
};

const SCOPE_PATH_PATTERN = /^(\/[a-zA-Z0-9\-_.*?]*)*\/?$/;

export const pathnamePortionOfScopePath = (path: string): string =>
  path.split(/[?#]/)[0];

export function scopePathEndsWithWildcard(path: string): boolean {
  return path.replace(/\/$/, '').endsWith('*');
}

export function isValidScopePath(path: string): boolean {
  if (!path.startsWith('/')) {
    return false;
  }

  const pathnamePortion = pathnamePortionOfScopePath(path);

  if (path.includes('?') || path.includes('#')) {
    if (pathnamePortion !== '/' && !SCOPE_PATH_PATTERN.test(pathnamePortion)) {
      return false;
    }

    if (
      pathnamePortion.includes('*') &&
      !pathHasWildcardOnlyAtSegmentStartOrMiddle(pathnamePortion)
    ) {
      return false;
    }

    return path.length > 1;
  }

  if (!pathHasWildcardOnlyAtSegmentStartOrMiddle(path)) {
    return false;
  }

  return SCOPE_PATH_PATTERN.test(path);
}

export function splitDomainHostAndPath(domainWithPath: string): {
  host: string;
  path?: string;
} {
  let trimmed = domainWithPath.trim();

  if (trimmed.toLowerCase().startsWith('//')) {
    trimmed = trimmed.slice(2);
  }

  trimmed = trimmed.replace(/^https?:\/\//i, '').replace(/\/$/, '');
  const slashIndex = trimmed.indexOf('/');

  if (slashIndex === -1) {
    return { host: trimmed.toLowerCase() };
  }

  return {
    host: trimmed.slice(0, slashIndex).toLowerCase(),
    path: trimmed.slice(slashIndex),
  };
}

export const isAllowlistRule = (rule: DomainPathRuleEntry): boolean =>
  rule.scope !== 'BLOCKLIST';

export const getAllowlistPathRules = (
  entry: DomainScopeEntry,
): DomainPathRuleEntry[] => (entry.pathRules ?? []).filter(isAllowlistRule);

export const getBlocklistPathRules = (
  entry: DomainScopeEntry,
): DomainPathRuleEntry[] =>
  (entry.pathRules ?? []).filter((rule) => !isAllowlistRule(rule));

export const getHostRestrictingAllowlistPathRules = (
  entry: DomainScopeEntry,
): DomainPathRuleEntry[] =>
  (entry.pathRules ?? []).filter(isHostRestrictingAllowlistRule);

export const getThirdPartyOverlayPathRules = (
  entry: DomainScopeEntry,
): DomainPathRuleEntry[] =>
  getAllowlistPathRules(entry).filter(isThirdPartyPathRule);

export const hasHostRestrictingAllowlistPathRules = (
  entry: DomainScopeEntry,
): boolean => getHostRestrictingAllowlistPathRules(entry).length > 0;

export const isDomainWideFunctionalityEnabled = (
  entry: DomainScopeEntry,
): boolean => entry.isInFunctionalityScope ?? false;

export const pathRuleInDimension = ({
  rule,
  dimension,
}: {
  rule: DomainPathRuleEntry;
  dimension: ScopeDimension;
}): boolean => {
  switch (dimension) {
    case 'functionality':
      return rule.isInTestingScope || rule.isInFunctionalityScope;
    case 'testing':
      return rule.isInTestingScope;
    case 'thirdParty':
      return isThirdPartyPathRule(rule);
  }
};

export function resolveDomainScopeFlags(
  entry: DomainScopeEntry,
): DomainScopeFlags {
  return resolveDomainScopeFlagsFromEntry({
    isInFunctionalityScope: entry.isInFunctionalityScope,
    isInTestingScope: entry.isInTestingScope,
    isThirdParty: entry.isThirdParty,
    allowlistRules: getHostRestrictingAllowlistPathRules(entry),
  });
}

export function domainEntryInDimension({
  entry,
  dimension,
}: {
  entry: DomainScopeEntry;
  dimension: ScopeDimension;
}): boolean {
  const flags = resolveDomainScopeFlags(entry);

  switch (dimension) {
    case 'functionality':
      return flags.isInFunctionalityScope;
    case 'testing':
      return flags.isInTestingScope;
    case 'thirdParty':
      return flags.isThirdParty;
  }
}

export function domainsForDimension({
  applicationDomains,
  dimension,
}: {
  applicationDomains: DomainScopeEntry[];
  dimension: ScopeDimension;
}): DomainScopeEntry[] {
  return applicationDomains.filter((entry) =>
    domainEntryInDimension({ entry, dimension }),
  );
}

export function toDomainScopeEntry(domain: {
  domain: string;
  isInFunctionalityScope?: boolean | null;
  isInTestingScope?: boolean | null;
  isThirdParty?: boolean | null;
  pathRules?: DomainPathRuleEntry[];
}): DomainScopeEntry {
  return {
    domain: domain.domain,
    isInFunctionalityScope: domain.isInFunctionalityScope ?? null,
    isInTestingScope: domain.isInTestingScope ?? null,
    isThirdParty: domain.isThirdParty ?? null,
    pathRules: domain.pathRules ?? [],
  };
}

export function testingScopeDomainsFromApplicationDomains(
  applicationDomains: Array<{
    domain: string;
    isInFunctionalityScope?: boolean | null;
    isInTestingScope?: boolean | null;
    isThirdParty?: boolean | null;
    pathRules?: DomainPathRuleEntry[];
  }>,
): DomainScopeEntry[] {
  return domainsForDimension({
    applicationDomains: applicationDomains.map(toDomainScopeEntry),
    dimension: 'testing',
  });
}

export function buildApplicationUrlScopeEntry({
  applicationUrl,
}: {
  applicationUrl: string;
}): DomainScopeEntry | null {
  const hostname = extractHostname(applicationUrl);

  if (!hostname) {
    return null;
  }

  const requestScopePath = extractRequestScopePath(applicationUrl);
  const resolvedPathRules =
    requestScopePath && requestScopePath !== '/'
      ? [
          {
            path: requestScopePath,
            scope: 'ALLOWLIST' as const,
            isInFunctionalityScope: true,
            isInTestingScope: true,
            isThirdParty: false,
          },
        ]
      : [];

  const hasAllowlist = resolvedPathRules.length > 0;

  return {
    domain: hostname,
    isInFunctionalityScope: hasAllowlist ? null : true,
    isInTestingScope: hasAllowlist ? null : true,
    pathRules: resolvedPathRules.length > 0 ? resolvedPathRules : undefined,
  };
}

export type ApplicationScopeSource = {
  applicationUrl: string;
  domains: Array<Parameters<typeof toDomainScopeEntry>[0]>;
};

function doesRequestHostMatchEntry({
  requestHost,
  host,
  isPattern,
}: {
  requestHost: string;
  host: string;
  isPattern: boolean;
}): boolean {
  if (isPattern) {
    return patternMatchesHost(host, requestHost);
  }

  const normalizedRequestHost = requestHost.toLowerCase();
  const normalizedHost = host.toLowerCase();

  if (normalizedRequestHost === normalizedHost) {
    return true;
  }

  if (normalizedHost !== 'localhost' && normalizedRequestHost !== 'localhost') {
    const wwwVariant = normalizedHost.startsWith('www.')
      ? normalizedHost.slice(4)
      : `www.${normalizedHost}`;

    return normalizedRequestHost === wwwVariant;
  }

  return false;
}

function scopePathMatchesWithoutQueryOrHashSuffix({
  requestPath,
  pattern,
}: {
  requestPath: string;
  pattern: string;
}): boolean {
  const patternPathname = pathnamePortionOfScopePath(pattern);
  const requestPathname = pathnamePortionOfScopePath(requestPath);

  if (patternPathname === '/' || patternPathname === '') {
    return true;
  }

  const patternPrefix = patternPathname.replace(/\/+$/, '');

  return (
    pathMatchesPattern(requestPathname, patternPathname) ||
    pathMatchesPattern(requestPathname, `${patternPrefix}/*`)
  );
}

export function doesPathMatchesScopePattern(
  requestPath: string,
  pattern: string,
): boolean {
  const normalizedRequestPath = requestPath.startsWith('/')
    ? requestPath
    : `/${requestPath}`;
  const normalizedPattern = pattern.startsWith('/') ? pattern : `/${pattern}`;

  if (!pathHasWildcardOnlyAtSegmentStartOrMiddle(normalizedPattern)) {
    return false;
  }

  if (
    (normalizedPattern.includes('#') && !normalizedRequestPath.includes('#')) ||
    (normalizedPattern.includes('?') && !normalizedRequestPath.includes('?'))
  ) {
    return scopePathMatchesWithoutQueryOrHashSuffix({
      requestPath: normalizedRequestPath,
      pattern: normalizedPattern,
    });
  }

  const requestPathForMatch =
    normalizedPattern.includes('?') || normalizedPattern.includes('#')
      ? normalizedRequestPath
      : pathnamePortionOfScopePath(normalizedRequestPath);

  if (normalizedPattern.includes('*')) {
    const regexBody = normalizedPattern
      .split('*')
      .map((segment) => segment.replace(/[.+?^${}()|[\]\\]/g, '\\$&'))
      .join('[^/]*');
    const regex = new RegExp(`^${regexBody}(/.*)?$`);

    return regex.test(requestPathForMatch);
  }

  return (
    pathMatchesPattern(requestPathForMatch, normalizedPattern) ||
    pathMatchesPattern(requestPathForMatch, `${normalizedPattern}/*`)
  );
}

function requestPathMatchesBlocklist(
  requestPath: string,
  entry: DomainScopeEntry,
): boolean {
  const blocklistRules = getBlocklistPathRules(entry);

  return blocklistRules.some((rule) =>
    doesPathMatchesScopePattern(requestPath, rule.path),
  );
}

function requestPathMatchesAllowlistForDimension({
  requestPath,
  entry,
  dimension,
}: {
  requestPath: string;
  entry: DomainScopeEntry;
  dimension: ScopeDimension;
}): boolean {
  return getHostRestrictingAllowlistPathRules(entry).some(
    (rule) =>
      pathRuleInDimension({ rule, dimension }) &&
      doesPathMatchesScopePattern(requestPath, rule.path),
  );
}

function requestPathMatchesThirdPartyRule({
  requestPath,
  entry,
}: {
  requestPath: string;
  entry: DomainScopeEntry;
}): boolean {
  return (entry.pathRules ?? []).some(
    (rule) =>
      rule.isThirdParty === true &&
      doesPathMatchesScopePattern(requestPath, rule.path),
  );
}

export function domainEntryMatchesUrl({
  entry,
  requestHost,
  requestPath,
  dimension,
}: {
  entry: DomainScopeEntry;
  requestHost: string;
  requestPath: string;
  dimension: ScopeDimension;
}): boolean {
  const { host } = splitDomainHostAndPath(entry.domain);

  if (
    !doesRequestHostMatchEntry({
      requestHost,
      host,
      isPattern: isRegexPattern(host),
    })
  ) {
    return false;
  }

  if (requestPathMatchesBlocklist(requestPath, entry)) {
    return false;
  }

  if (requestPathMatchesThirdPartyRule({ requestPath, entry })) {
    return dimension === 'thirdParty';
  }

  if (hasHostRestrictingAllowlistPathRules(entry)) {
    if (
      dimension === 'functionality' &&
      isDomainWideFunctionalityEnabled(entry)
    ) {
      return true;
    }

    return requestPathMatchesAllowlistForDimension({
      requestPath,
      entry,
      dimension,
    });
  }

  if (!domainEntryInDimension({ entry, dimension })) {
    return false;
  }

  const implicitPath = splitDomainHostAndPath(entry.domain).path;

  if (implicitPath) {
    return doesPathMatchesScopePattern(requestPath, implicitPath);
  }

  return true;
}

function doesRequestHostExactlyMatchEntry({
  requestHost,
  host,
  isPattern,
}: {
  requestHost: string;
  host: string;
  isPattern: boolean;
}): boolean {
  if (isPattern) {
    return false;
  }

  return requestHost.toLowerCase() === host.toLowerCase();
}

export function findMatchingDomainEntry({
  applicationDomains,
  requestHost,
  requestPath,
  dimension,
}: {
  applicationDomains: DomainScopeEntry[];
  requestHost: string;
  requestPath: string;
  dimension: ScopeDimension;
}): DomainScopeEntry | null {
  const exactHostEntries = applicationDomains.filter((entry) => {
    const { host } = splitDomainHostAndPath(entry.domain);

    return doesRequestHostExactlyMatchEntry({
      requestHost,
      host,
      isPattern: isRegexPattern(host),
    });
  });

  if (exactHostEntries.length > 0) {
    return (
      exactHostEntries.find((entry) =>
        domainEntryMatchesUrl({
          entry,
          requestHost,
          requestPath,
          dimension,
        }),
      ) ?? null
    );
  }

  return (
    applicationDomains.find((entry) =>
      domainEntryMatchesUrl({ entry, requestHost, requestPath, dimension }),
    ) ?? null
  );
}

function hostCoveredByDomainEntry({
  requestHost,
  host,
  isPattern,
}: {
  requestHost: string;
  host: string;
  isPattern: boolean;
}): boolean {
  if (isPattern) {
    return patternMatchesHost(host, requestHost);
  }

  const normalizedRequestHost = requestHost.toLowerCase();
  const normalizedHost = host.toLowerCase();

  if (normalizedRequestHost === normalizedHost) {
    return true;
  }

  return isSubdomain(normalizedRequestHost, normalizedHost);
}

export function isHostCoveredByScopedDomains({
  applicationDomains,
  requestHost,
  dimension,
}: {
  applicationDomains: DomainScopeEntry[];
  requestHost: string;
  dimension: ScopeDimension;
}): boolean {
  return applicationDomains.some((entry) => {
    if (!domainEntryInDimension({ entry, dimension })) {
      return false;
    }

    const { host } = splitDomainHostAndPath(entry.domain);

    return hostCoveredByDomainEntry({
      requestHost,
      host,
      isPattern: isRegexPattern(host),
    });
  });
}

export function isHostCoveredByAnyApplicationDomain({
  applicationDomains,
  requestHost,
}: {
  applicationDomains: DomainScopeEntry[];
  requestHost: string;
}): boolean {
  return applicationDomains.some((entry) => {
    const { host } = splitDomainHostAndPath(entry.domain);

    return hostCoveredByDomainEntry({
      requestHost,
      host,
      isPattern: isRegexPattern(host),
    });
  });
}

export function isInProxyApplicationScope({
  entry,
  requestHost,
  requestPath,
}: {
  entry: DomainScopeEntry;
  requestHost: string;
  requestPath: string;
}): boolean {
  return SCOPE_DIMENSIONS.some((dimension) =>
    domainEntryMatchesUrl({ entry, requestHost, requestPath, dimension }),
  );
}

function getApplicationUrlEntryPaths(
  applicationUrlEntry: DomainScopeEntry,
): string[] {
  const allowlistPaths = getHostRestrictingAllowlistPathRules(
    applicationUrlEntry,
  ).map((rule) => rule.path);

  if (allowlistPaths.length > 0) {
    return allowlistPaths;
  }

  const { path: embeddedPath } = splitDomainHostAndPath(
    applicationUrlEntry.domain,
  );

  return embeddedPath ? [embeddedPath] : [];
}

function domainEntryCoversApplicationUrlPaths({
  entry,
  applicationUrlHost,
  applicationUrlPaths,
}: {
  entry: DomainScopeEntry;
  applicationUrlHost: string;
  applicationUrlPaths: string[];
}): boolean {
  const { host: entryHost, path: entryEmbeddedPath } = splitDomainHostAndPath(
    entry.domain,
  );

  if (entryHost.toLowerCase() !== applicationUrlHost.toLowerCase()) {
    return false;
  }

  const allowlistPaths = getHostRestrictingAllowlistPathRules(entry).map(
    (rule) => rule.path,
  );

  if (entryEmbeddedPath && allowlistPaths.length === 0) {
    allowlistPaths.push(entryEmbeddedPath);
  }

  const entryIsWholeHost = allowlistPaths.length === 0 && !entryEmbeddedPath;

  if (entryIsWholeHost) {
    return true;
  }

  if (applicationUrlPaths.length === 0) {
    return !entryEmbeddedPath;
  }

  return applicationUrlPaths.every((applicationUrlPath) =>
    allowlistPaths.some((pattern) =>
      doesPathMatchesScopePattern(applicationUrlPath, pattern),
    ),
  );
}

function applicationUrlHostAlreadyRepresented({
  applicationUrlEntry,
  applicationDomains,
}: {
  applicationUrlEntry: DomainScopeEntry;
  applicationDomains: DomainScopeEntry[];
}): boolean {
  const { host: applicationUrlHost } = splitDomainHostAndPath(
    applicationUrlEntry.domain,
  );
  const applicationUrlPaths = getApplicationUrlEntryPaths(applicationUrlEntry);

  return applicationDomains.some((entry) =>
    domainEntryCoversApplicationUrlPaths({
      entry,
      applicationUrlHost,
      applicationUrlPaths,
    }),
  );
}

export function mergeApplicationUrlWithScopeEntries({
  applicationUrl,
  applicationDomains,
}: {
  applicationUrl: string;
  applicationDomains: DomainScopeEntry[];
}): DomainScopeEntry[] {
  const applicationUrlEntry = buildApplicationUrlScopeEntry({
    applicationUrl,
  });

  if (!applicationUrlEntry) {
    return [...applicationDomains];
  }

  if (
    applicationUrlHostAlreadyRepresented({
      applicationUrlEntry,
      applicationDomains,
    })
  ) {
    return [...applicationDomains];
  }

  return [applicationUrlEntry, ...applicationDomains];
}

export function resolveApplicationScope({
  applicationUrl,
  domains,
  dimension,
}: {
  applicationUrl: string;
  domains: Array<Parameters<typeof toDomainScopeEntry>[0]>;
  dimension?: ScopeDimension;
}): DomainScopeEntry[] {
  const mergedEntries = mergeApplicationUrlWithScopeEntries({
    applicationUrl,
    applicationDomains: domains.map(toDomainScopeEntry),
  });

  if (!dimension) {
    return mergedEntries;
  }

  return domainsForDimension({
    applicationDomains: mergedEntries,
    dimension,
  });
}

export function formatFunctionalityScopeEntries(
  domainsScope: DomainScopeEntry[],
): string[] {
  const entries: string[] = [];

  for (const entry of domainsScope) {
    const { host, path: embeddedPath } = splitDomainHostAndPath(entry.domain);

    if (isDomainWideFunctionalityEnabled(entry)) {
      if (embeddedPath) {
        entries.push(`${host}${embeddedPath}`);
      } else {
        entries.push(host);
      }

      continue;
    }

    const allowlistRules = (entry.pathRules ?? []).filter(
      (rule) =>
        isAllowlistRule(rule) &&
        pathRuleInDimension({ rule, dimension: 'functionality' }),
    );

    if (allowlistRules.length > 0) {
      for (const rule of allowlistRules) {
        entries.push(`${host}${rule.path}`);
      }

      continue;
    }

    if (!resolveDomainScopeFlags(entry).isInFunctionalityScope) {
      continue;
    }

    if (embeddedPath) {
      entries.push(`${host}${embeddedPath}`);
      continue;
    }

    entries.push(host);
  }

  return [...new Set(entries)];
}

export const PDF_THIRD_PARTY_SCOPE_LABEL = 'Third-party / communicate-only';

const formatThirdPartyPdfLabel = (target: string): string =>
  `${target} (${PDF_THIRD_PARTY_SCOPE_LABEL})`;

export function formatPdfAdditionalDomainLabels(
  domainsScope: DomainScopeEntry[],
): string[] {
  const functionalityLabels = formatFunctionalityScopeEntries(domainsScope);
  const thirdPartyLabels: string[] = [];

  for (const entry of domainsScope) {
    const { host, path: embeddedPath } = splitDomainHostAndPath(entry.domain);
    const overlayRules = getThirdPartyOverlayPathRules(entry);

    for (const rule of overlayRules) {
      thirdPartyLabels.push(formatThirdPartyPdfLabel(`${host}${rule.path}`));
    }

    if (getHostRestrictingAllowlistPathRules(entry).length > 0) {
      continue;
    }

    const flags = resolveDomainScopeFlags(entry);

    if (flags.isThirdParty && !flags.isInFunctionalityScope) {
      thirdPartyLabels.push(
        formatThirdPartyPdfLabel(
          embeddedPath ? `${host}${embeddedPath}` : host,
        ),
      );
    }
  }

  return [...new Set([...functionalityLabels, ...thirdPartyLabels])];
}

type ScopePromptLabel =
  | 'blocked'
  | 'functionality only'
  | 'testing'
  | 'third-party';

function scopePromptLabelFromFlags(
  flags: DomainScopeFlagsInput,
): ScopePromptLabel | null {
  switch (scopeModeFromFlags(flags)) {
    case 'attack':
      return 'testing';
    case 'interaction':
      return 'functionality only';
    case 'third_party':
      return 'third-party';
    case 'none':
      return null;
  }
}

function formatScopePromptTarget({
  host,
  path,
  hasBlocklistExceptions,
}: {
  host: string;
  path?: string;
  hasBlocklistExceptions?: boolean;
}): string {
  if (path) {
    return `${host}${path}`;
  }

  if (hasBlocklistExceptions) {
    return `${host} (all paths except blocked below)`;
  }

  return `${host} (all paths)`;
}

function collectScopePromptLines(
  applicationDomains: DomainScopeEntry[],
): string[] {
  const lines: string[] = [];

  for (const entry of applicationDomains) {
    const { host, path: embeddedPath } = splitDomainHostAndPath(entry.domain);
    const restrictingRules = getHostRestrictingAllowlistPathRules(entry);
    const overlayRules = getThirdPartyOverlayPathRules(entry);
    const blocklistRules = getBlocklistPathRules(entry);

    if (restrictingRules.length > 0) {
      if (isDomainWideFunctionalityEnabled(entry)) {
        lines.push(
          `- ${formatScopePromptTarget({
            host,
            hasBlocklistExceptions: blocklistRules.length > 0,
          })} — functionality only`,
        );
      }

      for (const rule of restrictingRules) {
        if (isDomainWideFunctionalityEnabled(entry) && !rule.isInTestingScope) {
          continue;
        }

        const label = scopePromptLabelFromFlags({
          isInFunctionalityScope: rule.isInFunctionalityScope,
          isInTestingScope: rule.isInTestingScope,
          isThirdParty: rule.isThirdParty ?? false,
        });

        if (!label) {
          continue;
        }

        lines.push(
          `- ${formatScopePromptTarget({ host, path: rule.path })} — ${label}`,
        );
      }
    } else {
      const label = scopePromptLabelFromFlags(resolveDomainScopeFlags(entry));

      if (label) {
        lines.push(
          `- ${formatScopePromptTarget({
            host,
            path: embeddedPath,
            hasBlocklistExceptions: !embeddedPath && blocklistRules.length > 0,
          })} — ${label}`,
        );
      }
    }

    for (const rule of overlayRules) {
      lines.push(
        `- ${formatScopePromptTarget({ host, path: rule.path })} — third-party`,
      );
    }

    for (const rule of blocklistRules) {
      lines.push(
        `- ${formatScopePromptTarget({ host, path: rule.path })} — blocked`,
      );
    }
  }

  return [...new Set(lines)];
}

export function formatScopeDimensionForPrompt({
  applicationDomains,
  dimension,
}: {
  applicationDomains: DomainScopeEntry[];
  dimension: ScopeDimension;
}): string {
  const lines: string[] = [];

  for (const entry of applicationDomains) {
    if (!domainEntryInDimension({ entry, dimension })) {
      continue;
    }

    const { host, path: embeddedPath } = splitDomainHostAndPath(entry.domain);
    const restrictingRules = (entry.pathRules ?? []).filter(
      (rule) =>
        isHostRestrictingAllowlistRule(rule) &&
        pathRuleInDimension({ rule, dimension }),
    );
    const blocklistRules = (entry.pathRules ?? []).filter(
      (rule) => !isAllowlistRule(rule),
    );
    const hasRestrictingAllowlist = hasHostRestrictingAllowlistPathRules(entry);
    const hasDomainWideFunctionality =
      dimension === 'functionality' &&
      hasRestrictingAllowlist &&
      isDomainWideFunctionalityEnabled(entry);

    if (hasDomainWideFunctionality) {
      lines.push(
        `- ${host}${
          blocklistRules.length > 0
            ? ' (all paths except blocked below)'
            : ' (all paths)'
        }`,
      );
    } else if (restrictingRules.length > 0) {
      for (const rule of restrictingRules) {
        lines.push(`- ${host}${rule.path}`);
      }
    } else if (!hasRestrictingAllowlist && embeddedPath) {
      lines.push(`- ${host}${embeddedPath}`);
    } else if (!hasRestrictingAllowlist) {
      lines.push(`- ${host} (all paths)`);
    }

    for (const rule of blocklistRules) {
      lines.push(`- ${host}${rule.path} (blocked)`);
    }
  }

  return [...new Set(lines)].join('\n');
}

export function formatApplicationScopeForPrompt({
  applicationUrl,
  domainsScope,
}: {
  applicationUrl: string;
  domainsScope: DomainScopeEntry[];
}): string {
  const mergedScope = mergeApplicationUrlWithScopeEntries({
    applicationUrl,
    applicationDomains: domainsScope,
  });
  const scopeLines = [
    ...collectScopePromptLines(mergedScope),
    ...TERRA_ALWAYS_ALLOWED_DOMAINS.map(
      (domain) => `- ${domain} (all paths) — functionality only`,
    ),
  ];

  return [
    '## Application Scope',
    '',
    'Send HTTP requests only to the entries below. All other domains and paths are forbidden.',
    '',
    'Each entry shows its scope:',
    '- testing — attack target (map assets, signals, and attacks)',
    '- functionality only — visit and discover for authentication, SSO, and navigation; create scoping assets only',
    '- third-party — communicate only; do not map endpoints, pages, or candidates',
    '- blocked — do not request this path',
    '',
    scopeLines.length > 0 ? scopeLines.join('\n') : '- (none configured)',
    '',
    'Testing entries are also reachable under functionality scope. Wildcard entries such as *.example.com allow any matching subdomain. Path entries include all subpaths beneath them when no other path restriction conflicts (for example /api matches /api/v2/users).',
  ].join('\n');
}
